import { matchPath } from 'react-router-dom';

/**
 * Resolves the header entry for a pathname from a route-keyed map, in three
 * steps:
 *
 *   1. an exact key ('/tenants');
 *   2. a parameterized key ('/regulatory/:jurisdictionCode') matched through
 *      react-router's matchPath, the same matcher the router uses;
 *   3. the longest literal prefix, so a detail route with no key of its own
 *      ('/tenants/<id>') reads its parent's entry.
 *
 * Step 2 runs before step 3. The layout used to do only 1 and 3, comparing
 * each key as a literal string, so '/regulatory/CA' never matched the
 * parameterized key and fell back to the '/regulatory' prefix: the
 * jurisdiction page read "Regulatory Readiness" in its header.
 */
export function resolvePageInfo<T>(
  pathname: string,
  pageInfo: Readonly<Record<string, T>>,
): T | undefined {
  const exact = pageInfo[pathname];
  if (exact) return exact;

  for (const key of Object.keys(pageInfo)) {
    if (key.includes(':') && matchPath({ path: key, end: true }, pathname)) {
      return pageInfo[key];
    }
  }

  const parts = pathname.split('/').filter(Boolean);
  while (parts.length > 0) {
    const entry = pageInfo[`/${parts.join('/')}`];
    if (entry) return entry;
    parts.pop();
  }
  return undefined;
}
